/**
 * Initializes DOM manipulation helpers on the provided context
 * @type {import('./index.d').initDom}
 */
export function initDom(context) {
  context.$ = function (query, dom) {
    if (
      (typeof query === 'string' && query.startsWith('.')) ||
      (typeof query === 'string' && query.startsWith('#')) ||
      (typeof query === 'string' && query.startsWith('['))
    )
      return querySelector(query, dom);

    function getById(id) {
      return document.getElementById(id);
    }

    function getByClass(className) {
      return document.getElementsByClassName(className);
    }

    function getByTag(tagName) {
      return document.getElementsByTagName(tagName);
    }
    function attr(target, attrName) {
      return target.getAttribute(attrName);
    }
    function querySelector(selector, dom) {
      if (!(dom instanceof Element)) {
        return context.document?.querySelector(selector);
      }
      return dom.querySelector(selector);
    }

    function querySelectorAll(selector) {
      return context.document.querySelectorAll(selector);
    }

    function addClass(element, className) {
      element.classList.add(className);
    }

    function removeClass(element, className) {
      element.classList.remove(className);
    }

    function toggleClass(element, className) {
      element.classList.toggle(className);
    }

    function append(parent, child) {
      parent.appendChild(child);
    }

    function on(element, event, handler) {
      element.addEventListener(event, handler);
    }

    function off(element, event, handler) {
      element.removeEventListener(event, handler);
    }

    function triggerEvent(element, eventName) {
      const event = new Event(eventName);
      element.dispatchEvent(event);
    }
    return {
      getById,
      getByClass,
      getByTag,
      attr,
      querySelector,
      querySelectorAll,
      addClass,
      removeClass,
      toggleClass,
      append,
      on,
      off,
      triggerEvent,
    };
  };
}

/**
 * Prevents all events from propagating
 * @type {import('./index.d').preventAllEvents}
 */
export const preventAllEvents = (e) => {
  // prevent all event
  e.stopImmediatePropagation();
  e.stopPropagation();
  e.preventDefault();
};

/**
 * Removes whitespace in div HTML elements
 * @type {import('./index.d').removeWhiteSpaceInDivHTML}
 */
export const removeWhiteSpaceInDivHTML = (html) => {
  const regex = /<div\s+([^<>]+?)\s*>(\s*)<\/div>/g;
  html = html.replace(regex, (match, attributes, content) => {
    if (content.trim() === '') {
      return `<div ${attributes}></div>`;
    } else {
      return match;
    }
  });
  return html;
};

/**
 * Announces a message to screen readers using aria-live regions
 * JavaScript version matching TypeScript implementation in dom.helper.ts
 * Uses separate regions for assertive and polite to prevent interruption
 * @param {string} message - The message to announce
 * @param {'polite'|'assertive'} priority - The priority level (default: 'assertive')
 * @param {number} clearAfterMs - Time in ms before removing the message (default: 3000)
 */
export function announceToScreenReader(message, priority = 'assertive', clearAfterMs = 3000) {
  if (!message) return;

  // Use separate aria-live regions for assertive vs polite to prevent conflicts
  const className =
    priority === 'assertive'
      ? 'boost-sd__sr-only boost-sd__filter-status-assertive'
      : 'boost-sd__sr-only boost-sd__filter-status-polite';

  // Find or create the aria-live region for this priority
  let liveRegion = document.querySelector(`.${className.replace(/ /g, '.')}`);

  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = className;
    liveRegion.setAttribute('role', priority === 'assertive' ? 'alert' : 'status');
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
  }

  // Clear any existing timeout
  const timeoutId = liveRegion._clearTimeout;
  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  // Set the message
  liveRegion.textContent = message;

  // Clear after delay
  if (clearAfterMs > 0) {
    liveRegion._clearTimeout = setTimeout(() => {
      liveRegion.textContent = '';
    }, clearAfterMs);
  }
}
